import React, { useState, useMemo } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    TextField,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    IconButton,
    InputAdornment,
    Chip,
    Select,
    MenuItem,
    Button,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    FormControl,
    FormLabel,
    styled,
    useTheme,
    Paper,
} from '@mui/material'
import { Icon } from 'packages/eid-icons'
import { history } from 'packages/core'

const StyledCard = styled(Card)(({ theme }) => ({
    cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    height: '240px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    border: `2px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    borderRadius: '16px',
    position: 'relative',
    overflow: 'hidden',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.2)',
        borderColor: theme.palette.primary.main,
        '& .workflow-icon': {
            transform: 'scale(1.15)',
            backgroundColor: theme.palette.primary.main,
            boxShadow: '0 8px 20px rgba(0, 0, 0, 0.2)',
        },
        '& .workflow-title': {
            color: theme.palette.primary.main,
        },
        '& .workflow-description': {
            color: theme.palette.text.primary,
        },
    },
    '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '6px',
        background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
        opacity: 0,
        transition: 'opacity 0.3s ease',
    },
    '&:hover::before': {
        opacity: 1,
    },
}))

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        maxWidth: '95vw',
        maxHeight: '95vh',
        width: '1400px',
        height: '900px',
        borderRadius: '16px',
        overflow: 'hidden',
    },
}))

const WorkflowIcon = styled(Box)(({ theme }) => ({
    width: '70px',
    height: '70px',
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.light,
    color: theme.palette.primary.contrastText,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    transition: 'all 0.3s ease',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    '& svg': {
        width: '36px',
        height: '36px',
    },
}))

// Define available workflows with their metadata and required props
const WORKFLOWS = [
    {
        name: 'OnboardZscalerApplicationSegment',
        displayName: 'Onboard Application Segment',
        description: 'Create and configure new application segments',
        category: 'Application Segment',
        icon: 'Applications',
        requiredProps: [
            {
                key: 'facilityResourceId',
                label: 'Facility Resource ID',
                type: 'text',
                placeholder: 'e.g., 12345',
            },
            {
                key: 'protectedApplicationResourceId',
                label: 'Protected Application Resource ID',
                type: 'text',
                placeholder: 'e.g., 67890',
            },
        ],
        optionalProps: [
            {
                key: 'returnUrl',
                label: 'Return URL',
                type: 'text',
                placeholder: '/applications',
            },
        ],
    },
    {
        name: 'ManageZscalerApplicationSegmentWizard',
        displayName: 'Manage Application Segment',
        description: 'Manage existing application segment configurations',
        category: 'Application Segment',
        icon: 'Settings',
        requiredProps: [
            {
                key: 'segmentId',
                label: 'Segment ID',
                type: 'text',
                placeholder: 'e.g., 12345',
            },
            {
                key: 'action',
                label: 'Action',
                type: 'select',
                options: [
                    'manageSegment',
                    'auditLogs',
                    'readOnlyConfiguration',
                ],
                placeholder: 'Select action',
            },
        ],
        optionalProps: [
            {
                key: 'protectedApplicationResourceId',
                label: 'Protected Application Resource ID',
                type: 'text',
                placeholder: 'e.g., 67890',
            },
            {
                key: 'returnUrl',
                label: 'Return URL',
                type: 'text',
                placeholder: '/applications',
            },
        ],
    },
    {
        name: 'RegisterExistingNetworkZone',
        displayName: 'Register Network Zone',
        description: 'Register existing network zones in the system',
        category: 'Network Zone',
        icon: 'Network',
        requiredProps: [
            {
                key: 'facilityResourceId',
                label: 'Facility Resource ID',
                type: 'text',
                placeholder: 'e.g., 12345',
            },
        ],
        optionalProps: [
            {
                key: 'returnUrl',
                label: 'Return URL',
                type: 'text',
                placeholder: '/networks',
            },
        ],
    },
    {
        name: 'ViewNetworkZone',
        displayName: 'View Network Zone',
        description: 'View and analyze network zone configurations',
        category: 'Network Zone',
        icon: 'View',
        requiredProps: [
            {
                key: 'resourceId',
                label: 'Resource ID',
                type: 'text',
                placeholder: 'e.g., 46556304',
            },
        ],
        optionalProps: [
            {
                key: 'returnUrl',
                label: 'Return URL',
                type: 'text',
                placeholder: '/networks',
            },
        ],
    },
    {
        name: 'ManageDeviceAssignmentsToNetworkSegments',
        displayName: 'Manage Device to Network Assignments',
        description: 'Assign devices to network segments',
        category: 'Network Management',
        icon: 'Devices',
        requiredProps: [
            {
                key: 'deviceName',
                label: 'Device Name',
                type: 'text',
                placeholder: 'e.g., TestDevice',
            },
            {
                key: 'networkZoneId',
                label: 'Network Zone ID',
                type: 'text',
                placeholder: 'e.g., 123',
            },
        ],
        optionalProps: [
            {
                key: 'returnUrl',
                label: 'Return URL',
                type: 'text',
                placeholder: '/networks',
            },
        ],
    },
    {
        name: 'ManageNetworkSegmentAssignmentsToDevices',
        displayName: 'Manage Network to Device Assignments',
        description: 'Assign network segments to devices',
        category: 'Network Management',
        icon: 'NetworkSegments',
        requiredProps: [
            {
                key: 'deviceName',
                label: 'Device Name',
                type: 'text',
                placeholder: 'e.g., TestDevice',
            },
            {
                key: 'networkZoneId',
                label: 'Network Zone ID',
                type: 'text',
                placeholder: 'e.g., 123',
            },
        ],
        optionalProps: [
            {
                key: 'returnUrl',
                label: 'Return URL',
                type: 'text',
                placeholder: '/networks',
            },
        ],
    },
]

const NativeWorkflowBrowser = ({ open, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('')
    const [selectedCategory, setSelectedCategory] = useState('All')
    const [selectedWorkflow, setSelectedWorkflow] = useState(null)
    const [workflowProps, setWorkflowProps] = useState({})
    const theme = useTheme()

    // Get unique categories
    const categories = useMemo(() => {
        const cats = ['All', ...new Set(WORKFLOWS.map((w) => w.category))]
        return cats
    }, [])

    // Filter workflows based on search term and category
    const filteredWorkflows = useMemo(() => {
        let filtered = WORKFLOWS

        if (selectedCategory !== 'All') {
            filtered = filtered.filter((w) => w.category === selectedCategory)
        }

        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase()
            filtered = filtered.filter(
                (w) =>
                    w.displayName.toLowerCase().includes(lowerCaseSearchTerm) ||
                    w.description.toLowerCase().includes(lowerCaseSearchTerm) ||
                    w.category.toLowerCase().includes(lowerCaseSearchTerm),
            )
        }

        return filtered
    }, [searchTerm, selectedCategory])

    const handleRunWorkflow = (workflowName, props = {}) => {
        // Build query string with workflow name, isNative, and any provided props
        const queryParams = new URLSearchParams({
            workflowName,
            isNative: 'true',
            ...props,
        })

        // Navigate to applications workflows page with all parameters
        history.push(`/applications/workflows?${queryParams.toString()}`)
        onClose()
    }

    const handleWorkflowClick = (workflow) => {
        setSelectedWorkflow(workflow)
        // Initialize props with empty values
        const initialProps = {}
        workflow.requiredProps?.forEach((prop) => {
            initialProps[prop.key] = ''
        })
        workflow.optionalProps?.forEach((prop) => {
            initialProps[prop.key] = ''
        })
        setWorkflowProps(initialProps)
    }

    const handlePropChange = (propKey, value) => {
        setWorkflowProps((prev) => ({
            ...prev,
            [propKey]: value,
        }))
    }

    const handleRunWithProps = () => {
        if (selectedWorkflow) {
            // Filter out empty props
            const filteredProps = Object.entries(workflowProps)
                .filter(([key, value]) => value && value.trim() !== '')
                .reduce((acc, [key, value]) => {
                    acc[key] = value
                    return acc
                }, {})

            handleRunWorkflow(selectedWorkflow.name, filteredProps)
            setSelectedWorkflow(null)
            setWorkflowProps({})
        }
    }

    const handleRunWithoutProps = () => {
        if (selectedWorkflow) {
            handleRunWorkflow(selectedWorkflow.name)
            setSelectedWorkflow(null)
            setWorkflowProps({})
        }
    }

    const renderPropInput = (prop) => {
        if (prop.type === 'select') {
            return (
                <Select
                    value={workflowProps[prop.key] || ''}
                    onChange={(e) => handlePropChange(prop.key, e.target.value)}
                    displayEmpty
                    fullWidth
                    size="small"
                >
                    <MenuItem value="">
                        <em>{prop.placeholder}</em>
                    </MenuItem>
                    {prop.options.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            )
        }

        return (
            <TextField
                fullWidth
                value={workflowProps[prop.key] || ''}
                onChange={(e) => handlePropChange(prop.key, e.target.value)}
                placeholder={prop.placeholder}
                variant="outlined"
                size="small"
            />
        )
    }

    return (
        <StyledDialog open={open} onClose={onClose} maxWidth={false}>
            <DialogTitle
                sx={{
                    pb: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                    color: 'white',
                }}
            >
                <Box display="flex" alignItems="center" gap={2}>
                    <Typography
                        variant="h5"
                        component="div"
                        sx={{ fontWeight: 'bold' }}
                    >
                        Native Workflow Browser
                    </Typography>
                    <Chip
                        label={`${filteredWorkflows.length} workflows`}
                        size="small"
                        sx={{
                            backgroundColor: 'rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            fontWeight: 'bold',
                        }}
                    />
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{ color: 'white' }}
                >
                    <Icon name="Close" />
                </IconButton>
            </DialogTitle>

            <DialogContent
                sx={{
                    p: 3,
                    backgroundColor: '#f8f9fa',
                    height: 'calc(100% - 80px)',
                    overflow: 'auto',
                }}
            >
                <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: '12px' }}>
                    <Box display="flex" gap={2} alignItems="center">
                        <TextField
                            fullWidth
                            variant="outlined"
                            placeholder="Search workflows... (e.g., 'network', 'application', 'manage')"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Icon name="Search" />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        />
                        <Select
                            value={selectedCategory}
                            onChange={(e) =>
                                setSelectedCategory(e.target.value)
                            }
                            displayEmpty
                            inputProps={{ 'aria-label': 'Select category' }}
                            sx={{
                                minWidth: 150,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: '8px',
                                },
                            }}
                        >
                            {categories.map((category) => (
                                <MenuItem key={category} value={category}>
                                    {category}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mt: 2 }}
                    >
                        Click on any workflow card to configure and run it.
                    </Typography>
                </Paper>

                <Grid container spacing={3}>
                    {filteredWorkflows.map((workflow) => (
                        <Grid item xs={12} sm={6} md={4} key={workflow.name}>
                            <StyledCard
                                onClick={() => handleWorkflowClick(workflow)}
                            >
                                <CardContent
                                    sx={{
                                        '&:last-child': { pb: 3 },
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        height: '100%',
                                        textAlign: 'center',
                                    }}
                                >
                                    <Box
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <WorkflowIcon className="workflow-icon">
                                            <Icon name="Play" />
                                        </WorkflowIcon>

                                        <Typography
                                            variant="h6"
                                            className="workflow-title"
                                            sx={{
                                                fontWeight: 'bold',
                                                mb: 1.5,
                                                color: theme.palette.text
                                                    .primary,
                                                transition: 'color 0.3s ease',
                                                fontSize: '1.8rem', // Match app's h6 size
                                                lineHeight: 1.3,
                                            }}
                                        >
                                            {workflow.displayName}
                                        </Typography>

                                        <Typography
                                            variant="body1"
                                            className="workflow-description"
                                            sx={{
                                                color: theme.palette.text
                                                    .secondary,
                                                lineHeight: 1.5,
                                                mb: 2,
                                                minHeight: '48px',
                                                display: '-webkit-box',
                                                WebkitLineClamp: 2,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                fontSize: '1.6rem', // Match app's body1 size
                                                fontWeight: 500,
                                                transition: 'color 0.3s ease',
                                            }}
                                        >
                                            {workflow.description}
                                        </Typography>
                                    </Box>

                                    <Box
                                        sx={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'center',
                                        }}
                                    >
                                        <Chip
                                            label={workflow.category}
                                            size="medium"
                                            color="primary"
                                            variant="outlined"
                                            sx={{
                                                fontWeight: 'bold',
                                                borderRadius: '20px',
                                                fontSize: '1.4rem', // Match app's body2 size
                                                height: '32px',
                                            }}
                                        />
                                    </Box>
                                </CardContent>
                            </StyledCard>
                        </Grid>
                    ))}
                </Grid>

                {filteredWorkflows.length === 0 && (
                    <Box
                        display="flex"
                        flexDirection="column"
                        alignItems="center"
                        justifyContent="center"
                        py={8}
                    >
                        <Typography
                            variant="h6"
                            color="textSecondary"
                            gutterBottom
                        >
                            No workflows found
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            Try a different search term or category
                        </Typography>
                    </Box>
                )}
            </DialogContent>

            {/* Workflow Configuration Dialog */}
            <Dialog
                open={Boolean(selectedWorkflow)}
                onClose={() => setSelectedWorkflow(null)}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        maxHeight: '90vh',
                    },
                }}
            >
                <DialogTitle
                    sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                        color: 'white',
                    }}
                >
                    <Box display="flex" alignItems="center" gap={2}>
                        <Icon name="Play" />
                        <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                            {selectedWorkflow?.displayName}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent
                    sx={{ p: 3, maxHeight: '70vh', overflow: 'auto' }}
                >
                    <Typography
                        variant="body2"
                        color="textSecondary"
                        sx={{ mb: 3 }}
                    >
                        {selectedWorkflow?.description}
                    </Typography>

                    {selectedWorkflow?.requiredProps &&
                        selectedWorkflow.requiredProps.length > 0 && (
                            <Accordion defaultExpanded sx={{ mb: 2 }}>
                                <AccordionSummary
                                    expandIcon={<Icon name="Arrow" />}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        color="error"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        Required Parameters
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        {selectedWorkflow.requiredProps.map(
                                            (prop) => (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sm={6}
                                                    key={prop.key}
                                                >
                                                    <FormControl fullWidth>
                                                        <FormLabel
                                                            required
                                                            sx={{
                                                                mb: 1,
                                                                fontWeight:
                                                                    'medium',
                                                            }}
                                                        >
                                                            {prop.label}
                                                        </FormLabel>
                                                        {renderPropInput(prop)}
                                                    </FormControl>
                                                </Grid>
                                            ),
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        )}

                    {selectedWorkflow?.optionalProps &&
                        selectedWorkflow.optionalProps.length > 0 && (
                            <Accordion sx={{ mb: 2 }}>
                                <AccordionSummary
                                    expandIcon={<Icon name="Arrow" />}
                                >
                                    <Typography
                                        variant="subtitle1"
                                        sx={{ fontWeight: 'bold' }}
                                    >
                                        Optional Parameters
                                    </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                    <Grid container spacing={2}>
                                        {selectedWorkflow.optionalProps.map(
                                            (prop) => (
                                                <Grid
                                                    item
                                                    xs={12}
                                                    sm={6}
                                                    key={prop.key}
                                                >
                                                    <FormControl fullWidth>
                                                        <FormLabel
                                                            sx={{
                                                                mb: 1,
                                                                fontWeight:
                                                                    'medium',
                                                            }}
                                                        >
                                                            {prop.label}
                                                        </FormLabel>
                                                        {renderPropInput(prop)}
                                                    </FormControl>
                                                </Grid>
                                            ),
                                        )}
                                    </Grid>
                                </AccordionDetails>
                            </Accordion>
                        )}

                    <Box
                        sx={{
                            mt: 3,
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Button
                            variant="outlined"
                            onClick={() => setSelectedWorkflow(null)}
                            sx={{ borderRadius: '8px' }}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleRunWithoutProps}
                            sx={{ borderRadius: '8px' }}
                        >
                            Run Without Props
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleRunWithProps}
                            disabled={selectedWorkflow?.requiredProps?.some(
                                (prop) =>
                                    !workflowProps[prop.key] ||
                                    workflowProps[prop.key].trim() === '',
                            )}
                            sx={{ borderRadius: '8px' }}
                        >
                            Run With Props
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </StyledDialog>
    )
}

export default NativeWorkflowBrowser
